import React, { useState, FormEvent, ChangeEvent, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { UserPlus, ArrowLeft, CheckCircle, Upload, Camera, Eye, EyeOff, User, Mail, Phone, MapPin, Briefcase, GraduationCap, Heart, AlertTriangle, ShieldCheck } from 'lucide-react';
import * as faceapi from 'face-api.js';
import type { MemberFormData } from '../types';
import { memberRegistrationSchema } from '../utils/validationSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { ErrorMessage } from '../components/ErrorMessage';

type FaceStatus = 'loading' | 'no-face' | 'not-centered' | 'not-straight' | 'too-far' | 'too-close' | 'ready';

const AUTO_CAPTURE_FRAMES = 10; // 10 × 300 ms ≈ 3 s hold-still countdown

const FACE_STATUS_CONFIG: Record<FaceStatus, { message: string; color: string }> = {
  loading:       { message: 'Loading face detection…',        color: 'text-gray-300' },
  'no-face':     { message: 'Position your face in the oval', color: 'text-white'    },
  'not-centered':{ message: 'Center your face in the oval',   color: 'text-yellow-400' },
  'not-straight':{ message: 'Look straight at the camera',    color: 'text-yellow-400' },
  'too-far':     { message: 'Move closer to the camera',      color: 'text-orange-400' },
  'too-close':   { message: 'Move further from the camera',   color: 'text-orange-400' },
  ready:         { message: '✓ Face aligned — ready to capture', color: 'text-green-400' },
};

interface RegisteredMember {
  memberId: string;
  fullName: string;
  email: string;
  status: string;
}

const MemberRegistration: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [registeredMember, setRegisteredMember] = useState<RegisteredMember | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isDetectingRef = useRef(false);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceStatus, setFaceStatus] = useState<FaceStatus>('loading');
  const [faceOffsets, setFaceOffsets] = useState({ visualDx: 0, visualDy: 0, faceRatio: 0, hasDetection: false });
  const alignedCountRef = useRef(0);
  const [alignedCount, setAlignedCount] = useState(0);
  const capturePhotoRef = useRef<() => void>(() => {});

  const [formData, setFormData] = useState<MemberFormData>({
    fullName: '',
    fatherName: '',
    email: '',
    gender: '',
    age: '',
    cnic: '',
    phone: '',
    address: '',
    city: '',
    occupation: '',
    organization: '',
    qualification: '',
    areasOfInterest: [],
    maritalStatus: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    declarationConsent: false,
    communicationConsent: false,
    password: '',
    department: '',
    studentId: '',
    university: '',
    profileImage: ''
  });

  // Initialize validation
  const { errors, validateField, validateForm, setError } = useFormValidation(memberRegistrationSchema);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      }
    };
  }, [stream]);

  const loadFaceModels = useCallback(async () => {
    if (modelsLoaded) return;
    try {
      const MODEL_URL = '/models';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
      setFaceStatus('no-face');
    } catch {
      // Models failed to load; camera still usable without guidance
      setFaceStatus('no-face');
    }
  }, [modelsLoaded]);

  const runFaceDetection = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2 || !modelsLoaded) return;

    const W = video.videoWidth || 640;
    const H = video.videoHeight || 480;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2;
    const cy = H / 2;
    const ovalW = W * 0.27; // half-width radius
    const ovalH = H * 0.40; // half-height radius

    // ── 1. Face Detection ──────────────────────────────────────────────
    let newStatus: FaceStatus = 'no-face';
    let newOffsets = { visualDx: 0, visualDy: 0, faceRatio: 0, hasDetection: false };
    let detectedBox: faceapi.Box | null = null;
    let detectedLandmarks: faceapi.FaceLandmarks68 | null = null;

    try {
      const det = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
        .withFaceLandmarks(true);

      if (det) {
        detectedBox = det.detection.box;
        detectedLandmarks = det.landmarks;

        const fcx = detectedBox.x + detectedBox.width  / 2;
        const fcy = detectedBox.y + detectedBox.height / 2;

        // visualDx: positive → face is RIGHT of oval on mirrored screen → show ← arrow
        // (canvas & video both have CSS scaleX(-1), so visual face x = W − fcx)
        const visualDx = (W / 2 - fcx) / W;
        const visualDy = (fcy - H / 2) / H;

        // Head tilt from eye-line angle
        const lPts = detectedLandmarks.getLeftEye();
        const rPts = detectedLandmarks.getRightEye();
        const lx  = lPts.reduce((s, p) => s + p.x, 0) / lPts.length;
        const ly  = lPts.reduce((s, p) => s + p.y, 0) / lPts.length;
        const rex = rPts.reduce((s, p) => s + p.x, 0) / rPts.length;
        const rey = rPts.reduce((s, p) => s + p.y, 0) / rPts.length;
        const tilt = Math.abs(Math.atan2(rey - ly, rex - lx) * (180 / Math.PI));

        const faceRatio = (detectedBox.width * detectedBox.height) / (W * H);
        const hOff = Math.abs(fcx - cx) / W;
        const vOff = Math.abs(fcy - cy) / H;

        newOffsets = { visualDx, visualDy, faceRatio, hasDetection: true };

        if      (faceRatio < 0.05)          newStatus = 'too-far';
        else if (faceRatio > 0.55)          newStatus = 'too-close';
        else if (hOff > 0.20 || vOff > 0.22) newStatus = 'not-centered';
        else if (tilt > 12)                 newStatus = 'not-straight';
        else                                newStatus = 'ready';
      }
    } catch {
      // Skip frame on error
    }

    // ── 2. Dark vignette with oval spotlight ───────────────────────────
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'destination-out'; // punch transparent hole
    ctx.beginPath();
    ctx.ellipse(cx, cy, ovalW, ovalH, 0, 0, 2 * Math.PI);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();

    // ── 3. Oval border ─────────────────────────────────────────────────
    const ovalBorderColor: Record<FaceStatus, string> = {
      loading:         'rgba(156,163,175,0.8)',
      'no-face':       'rgba(255,255,255,0.75)',
      'not-centered':  'rgba(251,191,36,1)',
      'not-straight':  'rgba(251,191,36,1)',
      'too-far':       'rgba(249,115,22,1)',
      'too-close':     'rgba(249,115,22,1)',
      ready:           'rgba(34,197,94,1)',
    };
    ctx.strokeStyle = ovalBorderColor[newStatus];
    ctx.lineWidth   = newStatus === 'ready' ? 5 : 3;
    if (newStatus === 'no-face') ctx.setLineDash([14, 7]);
    ctx.beginPath();
    ctx.ellipse(cx, cy, ovalW, ovalH, 0, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── 4. Corner-bracket marks on oval bounding box ───────────────────
    const bL = cx - ovalW; const bR = cx + ovalW;
    const bT = cy - ovalH; const bB = cy + ovalH;
    const bl = 18; // bracket arm length
    ctx.strokeStyle = ovalBorderColor[newStatus];
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    const brackets: [number, number, number, number, number, number][] = [
      [bL, bT + bl, bL, bT, bL + bl, bT],
      [bR - bl, bT, bR, bT, bR, bT + bl],
      [bL, bB - bl, bL, bB, bL + bl, bB],
      [bR - bl, bB, bR, bB, bR, bB - bl],
    ];
    brackets.forEach(([x1, y1, x2, y2, x3, y3]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.stroke();
    });

    // ── 5. Face bounding box (dashed) ──────────────────────────────────
    if (detectedBox) {
      ctx.strokeStyle = newStatus === 'ready' ? 'rgba(34,197,94,0.65)' : 'rgba(239,68,68,0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(detectedBox.x, detectedBox.y, detectedBox.width, detectedBox.height);
      ctx.setLineDash([]);
    }

    // ── 6. Facial landmarks (clipped to oval) ──────────────────────────
    if (detectedLandmarks) {
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(cx, cy, ovalW, ovalH, 0, 0, 2 * Math.PI);
      ctx.clip();
      ctx.fillStyle = newStatus === 'ready' ? 'rgba(34,197,94,0.8)' : 'rgba(239,68,68,0.6)';
      detectedLandmarks.positions.forEach(pt => {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 1.5, 0, 2 * Math.PI);
        ctx.fill();
      });
      ctx.restore();
    }

    // ── 7. Auto-capture progress ring ──────────────────────────────────
    if (newStatus === 'ready') {
      alignedCountRef.current += 1;
      const count = alignedCountRef.current;
      setAlignedCount(count);

      const progress = count / AUTO_CAPTURE_FRAMES;
      const ringR = Math.min(ovalW, ovalH) + 16;
      ctx.strokeStyle = 'rgba(34,197,94,0.95)';
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(cx, cy, ringR, -Math.PI / 2, -Math.PI / 2 + progress * 2 * Math.PI);
      ctx.stroke();

      if (count >= AUTO_CAPTURE_FRAMES) {
        alignedCountRef.current = 0;
        setAlignedCount(0);
        capturePhotoRef.current();
        return;
      }
    } else {
      if (alignedCountRef.current !== 0) {
        alignedCountRef.current = 0;
        setAlignedCount(0);
      }
    }

    setFaceStatus(newStatus);
    setFaceOffsets(newOffsets);
  }, [modelsLoaded]);

  // Start / stop detection loop with camera
  useEffect(() => {
    if (!showCamera) return;
    if (!modelsLoaded) {
      setFaceStatus('loading');
      return;
    }
    detectionTimerRef.current = setInterval(async () => {
      if (isDetectingRef.current) return;
      isDetectingRef.current = true;
      await runFaceDetection();
      isDetectingRef.current = false;
    }, 300);
    return () => {
      if (detectionTimerRef.current) {
        clearInterval(detectionTimerRef.current);
        detectionTimerRef.current = null;
      }
    };
  }, [showCamera, modelsLoaded, runFaceDetection]);

  const departments: string[] = [
    'Computer Science',
    'Engineering',
    'Business Administration',
    'Medicine',
    'Law',
    'Architecture',
    'Education',
    'Arts & Humanities',
    'Sciences',
    'Other'
  ];

  // Format CNIC
  const formatCNIC = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    if (numbers.length <= 12) return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(12, 13)}`;
  };

  // Format phone number
  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.startsWith('92')) {
      if (numbers.length <= 2) return `+${numbers}`;
      if (numbers.length <= 5) return `+${numbers.slice(0, 2)} ${numbers.slice(2)}`;
      return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)} ${numbers.slice(5, 12)}`;
    }
    if (numbers.startsWith('0')) {
      if (numbers.length <= 4) return numbers;
      return `${numbers.slice(0, 4)} ${numbers.slice(4, 11)}`;
    }
    return numbers;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    const target = e.target as HTMLInputElement;
    const { name, type } = target;
    let value: any = target.value;

    if (type === 'checkbox') {
      value = target.checked;
      setFormData(prev => ({ ...prev, [name]: value }));
      validateField(name as keyof MemberFormData, value);
      return;
    }

    if (name === 'cnic') value = formatCNIC(value);
    if (name === 'phone') value = formatPhone(value);

    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name as keyof MemberFormData, value);
  };

  const handleAreaToggle = (area: string): void => {
    setFormData(prev => {
      const current = prev.areasOfInterest;
      const updated = current.includes(area) ? current.filter(a => a !== area) : [...current, area];
      validateField('areasOfInterest', updated);
      return { ...prev, areasOfInterest: updated };
    });
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset so the same file can be re-selected after rejection
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    // Read file as base64
    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result ? String(reader.result) : '');
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Load face-api models if not already loaded
    let faceModelsReady = modelsLoaded;
    if (!faceModelsReady) {
      const loadToastId = toast.loading('Loading face detection…');
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
        faceModelsReady = true;
      } catch {
        // Models failed — skip face check rather than blocking the user
      }
      toast.dismiss(loadToastId);
    }

    if (faceModelsReady) {
      const img = await new Promise<HTMLImageElement>(resolve => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.src = base64String;
      });

      const validatingToastId = toast.loading('Checking photo for face…');
      try {
        const det = await faceapi
          .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
          .withFaceLandmarks(true);

        toast.dismiss(validatingToastId);

        if (!det) {
          toast.error('No face detected. Please upload a clear, straight face photo.');
          return;
        }

        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const faceRatio = (det.detection.box.width * det.detection.box.height) / (W * H);

        if (faceRatio < 0.04) {
          toast.error('Your face is too small in the photo. Please upload a closer face photo.');
          return;
        }

        // Tilt check using eye landmarks
        const lPts = det.landmarks.getLeftEye();
        const rPts = det.landmarks.getRightEye();
        const lx  = lPts.reduce((s, p) => s + p.x, 0) / lPts.length;
        const ly  = lPts.reduce((s, p) => s + p.y, 0) / lPts.length;
        const rex = rPts.reduce((s, p) => s + p.x, 0) / rPts.length;
        const rey = rPts.reduce((s, p) => s + p.y, 0) / rPts.length;
        const tilt = Math.abs(Math.atan2(rey - ly, rex - lx) * (180 / Math.PI));

        if (tilt > 15) {
          toast.error('Please upload a photo where you are looking straight at the camera (no head tilt).');
          return;
        }

      } catch {
        toast.dismiss(validatingToastId);
        // Detection error — allow upload rather than blocking
      }
    }

    // All checks passed
    setImagePreview(base64String);
    setFormData(prev => ({ ...prev, profileImage: base64String }));
  };

  const startCamera = async (): Promise<void> => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Your browser does not support camera access. Please use the Upload Photo option instead.');
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      setStream(mediaStream);
      setShowCamera(true);
      setFaceStatus('loading');
      loadFaceModels();

      setTimeout(() => {
        if (videoRef.current) {
          (videoRef.current as HTMLVideoElement).srcObject = mediaStream;
          void (videoRef.current as HTMLVideoElement).play();
        }
      }, 100);
    } catch (error: unknown) {
      console.error('Camera error:', error);

      if (error && typeof error === 'object' && 'name' in error) {
        const err = error as { name: string };
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          toast.error('Camera permission denied. Please allow camera access in your browser settings and try again.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          toast.error('No camera found. Please connect a camera or use the Upload Photo option.');
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          toast.error('Camera is already in use by another application. Please close other apps using the camera and try again.');
        } else if (err.name === 'OverconstrainedError') {
          toast.error('Camera settings are not supported. Please use the Upload Photo option.');
        } else if (err.name === 'SecurityError') {
          toast.error('Camera access requires a secure connection (HTTPS). Please use the Upload Photo option.');
        } else {
          toast.error('Unable to access camera. Please use the Upload Photo option instead.');
        }
      } else {
        toast.error('Unable to access camera. Please use the Upload Photo option instead.');
      }
    }
  };

  const capturePhoto = (): void => {
    if (videoRef.current) {
      const capCanvas = document.createElement('canvas');
      const v = videoRef.current;
      capCanvas.width  = v.videoWidth  || 640;
      capCanvas.height = v.videoHeight || 480;
      const ctx = capCanvas.getContext('2d');
      if (ctx) {
        // Mirror the capture to match what the user sees
        ctx.translate(capCanvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(v, 0, 0, capCanvas.width, capCanvas.height);
        const base64String = capCanvas.toDataURL('image/jpeg', 0.8);
        setImagePreview(base64String);
        setFormData(prev => ({ ...prev, profileImage: base64String }));
        stopCamera();
      }
    }
  };

  // Keep capturePhotoRef in sync so runFaceDetection can call it without stale closure
  useEffect(() => { capturePhotoRef.current = capturePhoto; });

  const stopCamera = (): void => {
    if (detectionTimerRef.current) {
      clearInterval(detectionTimerRef.current);
      detectionTimerRef.current = null;
    }
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setStream(null);
    }
    alignedCountRef.current = 0;
    setAlignedCount(0);
    setFaceOffsets({ visualDx: 0, visualDy: 0, faceRatio: 0, hasDetection: false });
    setShowCamera(false);
    setFaceStatus('loading');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Validate entire form before submission
    if (!validateForm(formData)) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Map camelCase to snake_case for backend
      const requestData: Record<string, any> = {
        full_name: formData.fullName,
        father_name: formData.fatherName,
        email: formData.email,
        gender: formData.gender,
        age: formData.age ? parseInt(formData.age) : undefined,
        cnic: formData.cnic,
        phone: formData.phone.replace(/[\s-]/g, ''),
        address: formData.address,
        city: formData.city || undefined,
        occupation: formData.occupation,
        organization: formData.organization,
        qualification: formData.qualification,
        areas_of_interest: formData.areasOfInterest,
        marital_status: formData.maritalStatus,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_relationship: formData.emergencyContactRelationship,
        emergency_contact_phone: formData.emergencyContactPhone,
        declaration_consent: formData.declarationConsent,
        communication_consent: formData.communicationConsent,
        password: formData.password,
        ...(formData.department && { department: formData.department }),
        ...(formData.studentId && { student_id: formData.studentId }),
        ...(formData.university && { university: formData.university }),
        ...(formData.profileImage && { profile_image: formData.profileImage }),
      };

      const response = await axios.post<any>('/v1/members/register', requestData);

      if (response.status === 200 || response.status === 201) {
        const memberData = response.data?.member || response.data;

        setRegisteredMember({
          memberId: memberData?.member_id || memberData?.memberId || 'Pending',
          fullName: memberData?.full_name || memberData?.fullName || formData.fullName,
          email: memberData?.email || formData.email,
          status: memberData?.status || 'pending'
        });

        setSuccess(true);
        toast.success('Registration successful! Please check your email to verify your account.');
      }
    } catch (error: any) {
      // Error is any
      console.error('Registration error:', error);

      if (error.response?.data?.errors) {
        // express-validator errors array
        const serverErrors = error.response.data.errors;
        const fieldMap: { [key: string]: keyof MemberFormData } = {
          full_name: 'fullName',
          father_name: 'fatherName',
          email: 'email',
          gender: 'gender',
          age: 'age',
          cnic: 'cnic',
          phone: 'phone',
          address: 'address',
          occupation: 'occupation',
          organization: 'organization',
          qualification: 'qualification',
          areas_of_interest: 'areasOfInterest',
          marital_status: 'maritalStatus',
          emergency_contact_name: 'emergencyContactName',
          emergency_contact_relationship: 'emergencyContactRelationship',
          emergency_contact_phone: 'emergencyContactPhone',
          declaration_consent: 'declarationConsent',
          communication_consent: 'communicationConsent',
          password: 'password',
          student_id: 'studentId',
          department: 'department',
          university: 'university',
          profile_image: 'profileImage',
        };
        serverErrors.forEach((err: any) => {
          const path = err.path || err.param || err.field;
          if (path) {
            const field: keyof MemberFormData = fieldMap[path] || (path as keyof MemberFormData);
            setError(field, err.msg || err.message);
          }
        });
        toast.error('Please fix the errors in the form');
      } else if (error.response?.data?.error || error.response?.data?.message) {
        const errorMsg: string = error.response.data.error || error.response.data.message;
        toast.error(errorMsg);

        // Map server conflict errors to the correct field
        const lower = errorMsg.toLowerCase();
        if (lower.includes('email')) {
          setError('email', errorMsg);
        } else if (lower.includes('cnic')) {
          setError('cnic', errorMsg);
        } else if (lower.includes('student id') || lower.includes('student_id')) {
          setError('studentId', errorMsg);
        } else if (lower.includes('phone')) {
          setError('phone', errorMsg);
        } else if (lower.includes('password')) {
          setError('password', errorMsg);
        }
      } else if (error.message) {
        toast.error(`Registration error: ${error.message}`);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success && registeredMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 flex items-center justify-center">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8 max-w-2xl w-full text-center border border-[#C49A3C]/20">
          <div className="text-[#0D5128] mb-4 sm:mb-6 flex justify-center">
            <div className="bg-[#0D5128]/10 p-3 sm:p-4 rounded-full">
              <CheckCircle size={48} className="sm:w-16 sm:h-16" />
            </div>
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0D5128] mb-3 sm:mb-4">Registration Successful!</h1>
          <p className="text-sm sm:text-base text-[#166534] mb-6 sm:mb-8">
            Thank you for registering with Hammad Foundation Volunteer Network.
          </p>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <div className="flex justify-between py-3 border-b border-green-200">
              <span className="text-sm font-semibold text-[#0D5128]">Name:</span>
              <span className="text-sm font-medium text-gray-900">{registeredMember.fullName}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-green-200">
              <span className="text-sm font-semibold text-[#0D5128]">Email:</span>
              <span className="text-sm font-medium text-gray-900">{registeredMember.email}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-sm font-semibold text-[#0D5128]">Status:</span>
              <span className="px-3 py-1 bg-[#C49A3C]/20 text-[#C49A3C] rounded-full text-xs font-medium">Pending Email Verification</span>
            </div>
          </div>
          <div className="bg-[#C49A3C]/10 border border-[#C49A3C]/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-[#0D5128] font-semibold mb-2">
              Important: Verify Your Email
            </p>
            <ul className="list-disc list-inside text-sm text-[#166534] space-y-1 text-left">
              <li>We've sent a verification email to {registeredMember.email}</li>
              <li>Click the verification link in the email to activate your account</li>
              <li>Once verified, you'll be automatically approved</li>
              <li>You'll receive a welcome email with your Member ID</li>
              <li>The verification link is valid for 24 hours</li>
            </ul>
          </div>
          <div className="flex gap-3 justify-center">
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors no-underline">
              <ArrowLeft size={20} />
              Back to Home
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-[#0D5128] hover:bg-green-800 text-white rounded-lg font-medium transition-colors no-underline">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 flex items-center justify-center">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8 max-w-2xl w-full relative border border-[#C49A3C]/20">
        <Link to="/" className="inline-flex items-center gap-1.5 sm:gap-2 text-[#0D5128] no-underline text-xs sm:text-sm mb-4 sm:mb-6 hover:text-green-800 transition-colors">
          <ArrowLeft size={16} className="sm:w-5 sm:h-5" />
          Back to Home
        </Link>

        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <div className="bg-[#0D5128] p-2 sm:p-3 rounded-xl sm:rounded-2xl">
              <img
                src="/website-04.png"
                alt="Hammad Foundation"
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain"
              />
            </div>
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0D5128] mb-1.5 sm:mb-2">Volunteer Registration</h1>
          <p className="text-xs sm:text-sm text-[#166534]">
            Join the Hammad Foundation Volunteer Network
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── SECTION 1: Personal Information ── */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#0D5128]/20">
              <User size={18} className="text-[#0D5128]" />
              <h3 className="font-semibold text-[#0D5128] text-sm uppercase tracking-wide">Personal Information</h3>
            </div>
            <div className="space-y-4">

              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.fullName ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="Enter your full name" />
                <ErrorMessage message={errors.fullName} />
              </div>

              {/* Father's Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Father's Name *</label>
                <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.fatherName ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="Enter your father's name" />
                <ErrorMessage message={errors.fatherName} />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange}
                    className={`block w-full pl-9 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.email ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="your.email@example.com" />
                </div>
                <ErrorMessage message={errors.email} />
              </div>

              {/* Gender & Age side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Gender *</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}
                    className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.gender ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <ErrorMessage message={errors.gender} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Age *</label>
                  <input type="number" name="age" value={formData.age} onChange={handleChange} min="10" max="100"
                    className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.age ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="Age" />
                  <ErrorMessage message={errors.age} />
                </div>
              </div>

              {/* CNIC */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">CNIC / National ID Number *</label>
                <input type="text" name="cnic" value={formData.cnic} onChange={handleChange} maxLength={15}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.cnic ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="12345-1234567-1" />
                <ErrorMessage message={errors.cnic} />
                {!errors.cnic && formData.cnic.length === 15 && <p className="text-xs text-green-600 mt-1">✓ Valid CNIC format</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contact Number *</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className={`block w-full pl-9 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.phone ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="03001234567" />
                </div>
                <ErrorMessage message={errors.phone} />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Residential Address *</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
                  <textarea name="address" value={formData.address} onChange={handleChange} rows={2}
                    className={`block w-full pl-9 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm resize-none ${errors.address ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="House #, Street, City" />
                </div>
                <ErrorMessage message={errors.address} />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">City *</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" name="city" value={formData.city} onChange={handleChange}
                    className={`block w-full pl-9 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.city ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="e.g. Karachi, Lahore, Islamabad" />
                </div>
                <ErrorMessage message={errors.city} />
              </div>

            </div>
          </div>

          {/* ── SECTION 2: Professional / Educational Information ── */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#0D5128]/20">
              <Briefcase size={18} className="text-[#0D5128]" />
              <h3 className="font-semibold text-[#0D5128] text-sm uppercase tracking-wide">Professional / Educational Information</h3>
            </div>
            <div className="space-y-4">

              {/* Occupation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Occupation / Profession *</label>
                <input type="text" name="occupation" value={formData.occupation} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.occupation ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="e.g. Student, Engineer, Teacher" />
                <ErrorMessage message={errors.occupation} />
              </div>

              {/* Organization */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Organization / Institution *</label>
                <input type="text" name="organization" value={formData.organization} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.organization ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="e.g. University of Karachi, ABC Company" />
                <ErrorMessage message={errors.organization} />
              </div>

              {/* Qualification */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Qualification / Education Level *</label>
                <select name="qualification" value={formData.qualification} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.qualification ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}>
                  <option value="">Select qualification</option>
                  <option value="Matriculation">Matriculation (SSC)</option>
                  <option value="Intermediate">Intermediate (HSSC / FA / FSc)</option>
                  <option value="Bachelor's">Bachelor's (BA / BSc / BBA / BE)</option>
                  <option value="Master's">Master's (MA / MSc / MBA / MEng)</option>
                  <option value="MPhil">MPhil</option>
                  <option value="PhD">PhD</option>
                  <option value="Other">Other</option>
                </select>
                <ErrorMessage message={errors.qualification} />
              </div>

              {/* Areas of Interest */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Areas of Interest in Foundation's Work *</label>
                <div className="grid grid-cols-2 gap-2">
                  {['health', 'education', 'social work', 'fundraising', 'environment', 'youth development'].map(area => (
                    <label key={area} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all text-sm ${
                      formData.areasOfInterest.includes(area)
                        ? 'bg-[#0D5128]/10 border-[#0D5128] text-[#0D5128] font-medium'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[#0D5128]/40'
                    }`}>
                      <input type="checkbox" checked={formData.areasOfInterest.includes(area)} onChange={() => handleAreaToggle(area)} className="hidden" />
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${formData.areasOfInterest.includes(area) ? 'bg-[#0D5128] border-[#0D5128]' : 'border-gray-400'}`}>
                        {formData.areasOfInterest.includes(area) && <CheckCircle size={10} className="text-white" />}
                      </div>
                      <span className="capitalize">{area}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage message={errors.areasOfInterest} />
              </div>

            </div>
          </div>

          {/* ── SECTION 3: Emergency Contact ── */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#0D5128]/20">
              <AlertTriangle size={18} className="text-[#0D5128]" />
              <h3 className="font-semibold text-[#0D5128] text-sm uppercase tracking-wide">Emergency Contact</h3>
            </div>
            <div className="space-y-4">

              {/* Marital Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Marital Status *</label>
                <div className="flex gap-3">
                  {['single', 'married'].map(ms => (
                    <label key={ms} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border cursor-pointer transition-all text-sm font-medium ${
                      formData.maritalStatus === ms ? 'bg-[#0D5128]/10 border-[#0D5128] text-[#0D5128]' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[#0D5128]/40'
                    }`}>
                      <input type="radio" name="maritalStatus" value={ms} checked={formData.maritalStatus === ms} onChange={handleChange} className="hidden" />
                      <Heart size={14} className={formData.maritalStatus === ms ? 'text-[#0D5128]' : 'text-gray-400'} />
                      <span className="capitalize">{ms}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage message={errors.maritalStatus} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Emergency Contact Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Name of Emergency Contact *</label>
                  <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange}
                    className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.emergencyContactName ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="Full name" />
                  <ErrorMessage message={errors.emergencyContactName} />
                </div>

                {/* Relationship */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Relationship *</label>
                  <select name="emergencyContactRelationship" value={formData.emergencyContactRelationship} onChange={handleChange}
                    className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.emergencyContactRelationship ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}>
                    <option value="">Select relationship</option>
                    <option value="father">Father</option>
                    <option value="mother">Mother</option>
                    <option value="spouse">Spouse</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                  <ErrorMessage message={errors.emergencyContactRelationship} />
                </div>
              </div>

              {/* Emergency Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Emergency Contact Phone *</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange}
                    className={`block w-full pl-9 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm ${errors.emergencyContactPhone ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                    placeholder="03001234567" />
                </div>
                <ErrorMessage message={errors.emergencyContactPhone} />
              </div>

            </div>
          </div>

          {/* ── SECTION 4: Account Setup ── */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#0D5128]/20">
              <GraduationCap size={18} className="text-[#0D5128]" />
              <h3 className="font-semibold text-[#0D5128] text-sm uppercase tracking-wide">Account Setup</h3>
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password *</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                  className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all text-sm pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                  placeholder="Create a strong password" />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <ErrorMessage message={errors.password} />
              {!errors.password && formData.password && (
                <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {[
                    [formData.password.length >= 8, '8+ characters'],
                    [/[A-Z]/.test(formData.password), 'Uppercase letter'],
                    [/[a-z]/.test(formData.password), 'Lowercase letter'],
                    [/[0-9]/.test(formData.password), 'Number']
                  ].map(([ok, label], i) => (
                    <p key={i} className={`text-xs ${ok ? 'text-green-600' : 'text-gray-400'}`}>
                      {ok ? '✓' : '○'} {label as string}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Profile Image Upload - REQUIRED */}
          <div>
            <label className="block text-sm font-semibold text-[#0D5128] mb-2">
              Profile Photo * <span className="text-red-600 text-xs">(Required for membership card)</span>
            </label>

            {showCamera && (() => {
              const secsLeft = Math.ceil((AUTO_CAPTURE_FRAMES - alignedCount) * 0.3);
              const showLeft  = faceOffsets.hasDetection && faceStatus === 'not-centered' && faceOffsets.visualDx >  0.10;
              const showRight = faceOffsets.hasDetection && faceStatus === 'not-centered' && faceOffsets.visualDx < -0.10;
              const showUp    = faceOffsets.hasDetection && faceStatus === 'not-centered' && faceOffsets.visualDy >  0.10;
              const showDown  = faceOffsets.hasDetection && faceStatus === 'not-centered' && faceOffsets.visualDy < -0.10;
              return (
                <div className="mb-3 sm:mb-4 rounded-lg overflow-hidden bg-black select-none">
                  <div className="relative">
                    {/* Live camera feed */}
                    <video
                      ref={videoRef}
                      autoPlay playsInline muted
                      className="w-full block"
                      style={{ transform: 'scaleX(-1)', maxHeight: '380px', objectFit: 'cover' }}
                    />

                    {/* Canvas: vignette + oval + landmarks (same CSS flip so coords align) */}
                    <canvas
                      ref={canvasRef}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{ transform: 'scaleX(-1)' }}
                    />

                    {/* ── HTML overlays (no transform — screen coords) ── */}

                    {/* Models loading */}
                    {faceStatus === 'loading' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mb-2" />
                        <span className="text-white text-xs font-medium drop-shadow">Loading face detection…</span>
                      </div>
                    )}

                    {/* Status badge – top centre */}
                    {faceStatus !== 'loading' && (
                      <div className="absolute top-3 left-0 right-0 flex justify-center pointer-events-none">
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm shadow ${FACE_STATUS_CONFIG[faceStatus].color}`}>
                          {FACE_STATUS_CONFIG[faceStatus].message}
                        </span>
                      </div>
                    )}

                    {/* Directional arrows — only when face detected but not centered */}
                    {showLeft && (
                      <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none animate-pulse">
                        <div className="bg-yellow-500/80 rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl font-black">←</span>
                        </div>
                      </div>
                    )}
                    {showRight && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none animate-pulse">
                        <div className="bg-yellow-500/80 rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl font-black">→</span>
                        </div>
                      </div>
                    )}
                    {showUp && (
                      <div className="absolute top-10 left-1/2 -translate-x-1/2 pointer-events-none animate-pulse">
                        <div className="bg-yellow-500/80 rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl font-black">↑</span>
                        </div>
                      </div>
                    )}
                    {showDown && (
                      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none animate-pulse">
                        <div className="bg-yellow-500/80 rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl font-black">↓</span>
                        </div>
                      </div>
                    )}

                    {/* Distance hint */}
                    {faceOffsets.hasDetection && (faceStatus === 'too-far' || faceStatus === 'too-close') && (
                      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none animate-pulse">
                        <div className="bg-orange-500/80 rounded-full px-4 py-1.5 shadow-lg">
                          <span className="text-white text-sm font-bold">
                            {faceStatus === 'too-far' ? '⊕ Move Closer' : '⊖ Move Back'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Tilt hint */}
                    {faceStatus === 'not-straight' && (
                      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none animate-pulse">
                        <div className="bg-yellow-500/80 rounded-full px-4 py-1.5 shadow-lg">
                          <span className="text-white text-sm font-bold">↔ Keep Head Straight</span>
                        </div>
                      </div>
                    )}

                    {/* Auto-capture countdown */}
                    {faceStatus === 'ready' && alignedCount > 0 && (
                      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none">
                        <div className="bg-green-600/85 rounded-full px-4 py-1.5 shadow-lg">
                          <span className="text-white text-sm font-bold">
                            {secsLeft <= 0 ? 'Capturing…' : `Hold still… ${secsLeft}s`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Controls */}
                  <div className="flex gap-2 p-2 bg-white">
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={faceStatus !== 'ready'}
                      title={faceStatus !== 'ready' ? FACE_STATUS_CONFIG[faceStatus].message : 'Capture photo'}
                      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg text-sm font-semibold transition-all ${
                        faceStatus === 'ready'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-gray-400 cursor-not-allowed opacity-55'
                      }`}
                    >
                      <Camera size={16} />
                      {faceStatus === 'ready' ? 'Capture Photo' : 'Align Face to Capture'}
                    </button>
                    <button
                      type="button"
                      onClick={stopCamera}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {!showCamera && (
              <>
                {imagePreview ? (
                  <div className="mb-4 flex flex-col items-center gap-3 p-4 bg-secondary-50 rounded-lg border-2 border-secondary-200">
                    <img
                      src={imagePreview}
                      alt="Profile preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-primary-200 shadow-lg"
                    />
                    <div className="flex gap-2">
                      <label className="btn btn-secondary cursor-pointer">
                        <Upload size={18} />
                        Change Photo
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                          tabIndex={-1}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={startCamera}
                        className="btn btn-secondary"
                      >
                        <Camera size={18} />
                        Retake
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={`p-6 border-2 border-dashed rounded-lg text-center ${errors.profileImage ? 'border-red-500 bg-red-50' : 'border-secondary-300 bg-secondary-50'}`}>
                    <div className="mb-4">
                      <div className="w-24 h-24 mx-auto bg-secondary-200 rounded-full flex items-center justify-center mb-3">
                        <Upload className="w-10 h-10 text-secondary-500" />
                      </div>
                      <p className="text-sm text-[#0D5128] font-medium mb-1">Add your profile photo</p>
                      <p className="text-xs text-secondary-600">This will appear on your member card and certificates</p>
                    </div>
                    <div className="flex gap-2 justify-center flex-wrap">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#C49A3C] hover:bg-[#B8903A] text-white rounded-lg font-medium cursor-pointer transition-all">
                        <Upload size={18} />
                        Upload Photo
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                          tabIndex={-1}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={startCamera}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D5128] hover:bg-green-800 text-white rounded-lg font-medium transition-all"
                      >
                        <Camera size={18} />
                        Take Photo
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <ErrorMessage message={errors.profileImage} />
            {!errors.profileImage && !imagePreview && (
              <p className="text-xs text-secondary-500 mt-1">Maximum size: 2MB. Formats: JPG, PNG</p>
            )}
          </div>

          {/* Student Information Section */}
          <div className="border-t border-secondary-200 pt-3 sm:pt-4 lg:pt-5 mt-4 sm:mt-5 lg:mt-6">
            <h3 className="text-sm sm:text-base font-semibold text-secondary-900 mb-0.5 sm:mb-1">
              Student Information <span className="text-secondary-500 font-normal text-xs sm:text-sm">(Optional)</span>
            </h3>
            <p className="text-[10px] sm:text-xs text-secondary-600 mb-3 sm:mb-4">If you're a student, please provide the following details</p>

            {/* Department */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[#0D5128] mb-2">Department</label>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                className={`block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                errors.department ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#0D5128]'}`}
              >
                <option value="">Select your department (if student)</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
              <ErrorMessage message={errors.department} />
            </div>

            {/* Student ID */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[#0D5128] mb-2">Student ID</label>
              <input
                type="text"
                name="studentId"
                value={formData.studentId}
                onChange={handleChange}
                className={`block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                errors.studentId ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                placeholder="Enter your student ID (if applicable)"
              />
              <ErrorMessage message={errors.studentId} />
            </div>

            {/* University */}
            <div>
              <label className="block text-sm font-semibold text-[#0D5128] mb-2">University</label>
              <input
                type="text"
                name="university"
                value={formData.university}
                onChange={handleChange}
                className={`block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                errors.university ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#0D5128]'}`}
                placeholder="Enter your university name (if applicable)"
              />
              <ErrorMessage message={errors.university} />
            </div>
          </div>

          {/* ── SECTION: Declaration & Consent ── */}
          <div className="border border-[#C49A3C]/40 rounded-xl p-5 bg-[#C49A3C]/5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#C49A3C]/30">
              <ShieldCheck size={18} className="text-[#C49A3C]" />
              <h3 className="font-semibold text-[#0D5128] text-sm uppercase tracking-wide">Declaration & Consent</h3>
            </div>

            {/* Declaration */}
            <div className="mb-4">
              <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                I confirm that all information I provided is true. If I am found involved in any activity against the rules, values, or reputation of Hammad Foundation, the organization has the right to terminate my membership/registration at any time.
              </p>
              <div className="flex gap-4">
                {[
                  { val: true, label: 'Yes, I agree' },
                  { val: false, label: 'No' }
                ].map(({ val, label }) => (
                  <label key={label} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer text-sm font-medium transition-all ${
                    formData.declarationConsent === val && (val || !formData.declarationConsent)
                      ? val ? 'bg-[#0D5128]/10 border-[#0D5128] text-[#0D5128]' : 'bg-red-50 border-red-300 text-red-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="declarationConsent"
                      checked={formData.declarationConsent === val}
                      onChange={() => {
                        setFormData(prev => ({ ...prev, declarationConsent: val }));
                        validateField('declarationConsent', val);
                      }}
                      className="hidden"
                    />
                    <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${formData.declarationConsent === val ? (val ? 'border-[#0D5128] bg-[#0D5128]' : 'border-red-400 bg-red-400') : 'border-gray-300'}`}>
                      {formData.declarationConsent === val && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    {label}
                  </label>
                ))}
              </div>
              <ErrorMessage message={errors.declarationConsent} />
            </div>

            {/* Communication Consent */}
            <div>
              <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                I consent to Hammad Foundation using my provided information for official communication, updates, announcements, and activities related to the organization.
              </p>
              <div className="flex gap-4">
                {[
                  { val: true, label: 'Yes' },
                  { val: false, label: 'No' }
                ].map(({ val, label }) => (
                  <label key={label} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer text-sm font-medium transition-all ${
                    formData.communicationConsent === val
                      ? val ? 'bg-[#0D5128]/10 border-[#0D5128] text-[#0D5128]' : 'bg-red-50 border-red-300 text-red-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="communicationConsent"
                      checked={formData.communicationConsent === val}
                      onChange={() => {
                        setFormData(prev => ({ ...prev, communicationConsent: val }));
                        validateField('communicationConsent', val);
                      }}
                      className="hidden"
                    />
                    <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${formData.communicationConsent === val ? (val ? 'border-[#0D5128] bg-[#0D5128]' : 'border-red-400 bg-red-400') : 'border-gray-300'}`}>
                      {formData.communicationConsent === val && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    {label}
                  </label>
                ))}
              </div>
              <ErrorMessage message={errors.communicationConsent} />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D5128] hover:bg-green-800 text-white font-semibold py-3 sm:py-3.5 lg:py-4 rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mt-3 sm:mt-4 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                <span className="text-xs sm:text-base">Processing...</span>
              </>
            ) : (
              <>
                <UserPlus size={18} className="sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Register as Volunteer</span>
              </>
            )}
          </button>

          <p className="text-[10px] sm:text-xs text-center text-secondary-600 mt-3 sm:mt-4">
            Already have an account?{' '}
            <Link to="/member/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Login here
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default MemberRegistration;
